import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams, useSearchParams } from 'react-router-dom'
import dayjs from 'dayjs'
import useDirectory from 'hooks/useDirectory'
import { Building } from 'models/Building'
import useSWR from 'swr'
import { AnnotationType, CustomFolder, Folders } from 'types/building'
import Utils from 'utils'
import CommonFunction from 'utils/commonFunction'

import RightArrowIcon from 'components/icons/RightArrowIcon'
import ViewerHeader from 'components/organisms/Header/ViewerHeader'

import ListChildrenFolder from './components/ListChildrenFolder'
import ListFile from './components/ListFile'
import ListFolder from './components/ListFolder'

export default function BuildingFileList() {
  const { id } = useParams()
  const [childrenFolder, setChildrenFolder] = useState('')
  const [searchParams, setSearchParams] = useSearchParams()
  const folderType = searchParams.get('folder')
  const childrenType = searchParams.get('children')
  const childrenFolderType = searchParams.get('children_folder')
  const keywordSearch = searchParams.get('keyword')

  const breadcrumbState = {
    folder: Boolean(folderType || childrenType),
    children: Boolean(childrenType),
    children_folder: Boolean(childrenFolderType),
    keyword: Boolean(keywordSearch)
  }

  const {
    navigate,
    getBuiildingType,
    getDirectory,
    getBuiildingFolderList,
    getDirectoryPrimaryColor
  } = useDirectory()

  const { t } = useTranslation()

  const { data: building } = useSWR<Building>(
    {
      url: `/v1/buildings/by-type?id=${id}&building_type=${getBuiildingType()}`
    },
    {
      onError: (err) => {
        Utils.handleErrorNavigate(err, navigate)
      }
    }
  )

  const { data: bcpCustomFolders, isValidating } = useSWR<CustomFolder[]>(
    getDirectory() === 'bcp' ? { url: `v1/bcp/buildings/${id}/folders` } : null
  )

  const { data: listNumberOfFile } = useSWR({
    url: `v1/buildings/categories-files?building_id=${id}`
  })

  const annotationTypeValues = Object.values(AnnotationType).filter(
    (v) => typeof v === 'number'
  )

  const listFolder = useMemo((): Folders[] => {
    if (getDirectory() === 'bcp') {
      if (!isValidating) {
        return [
          ...getBuiildingFolderList(),
          ...(bcpCustomFolders
            ?.sort((a: any, b: any) =>
              dayjs(a.createdAt).diff(dayjs(b.createdAt))
            )
            .map((item: any) => ({
              id: item.id,
              label: item.name,
              isFileHolder: false,
              isCustomFolder: true,
              children: item.childFolders.map((folder: any) => ({
                id: folder.id,
                categoryId: folder.categoryId,
                label: folder.name,
                isFileHolder: true,
                children: null
              }))
            })) || [])
        ]
      }
      return []
    }
    return getBuiildingFolderList()
  }, [bcpCustomFolders, getBuiildingFolderList, getDirectory, isValidating])

  const convertedListFolder = useMemo(() => {
    if (listNumberOfFile?.data) {
      const keys = Object.keys(listNumberOfFile.data)
      const folders = listFolder.map((folder) => {
        const folderKeys = keys.filter(
          (key) => +key - folder.id >= 0 && +key - folder.id <= 4
        )
        const numberOfFile = folderKeys.reduce((count, key) => {
          return count + listNumberOfFile.data[key]
        }, 0)
        return {
          ...folder,
          numberOfFile,
          children:
            folder.children?.map((item) => ({
              ...item,
              numberOfFile: listNumberOfFile.data[item.id] || 0
            })) || []
        }
      })
      return folders
    }
    return []
  }, [listNumberOfFile, listFolder])

  const handleBack = (keepFolder = false) => {
    const newParams = new URLSearchParams(searchParams)
    if (!keepFolder) newParams.delete('folder')
    newParams.delete('children')
    newParams.delete('children_folder')
    newParams.delete('keyword')
    setSearchParams(newParams)
  }

  const [hoverState, setHoverState] = useState({
    title: false,
    folder: false
  })

  return (
    <div className="h-screen grid grid-rows-[auto_1fr] overflow-x-hidden bg-[#F9FBFF]">
      <ViewerHeader building={building} />

      <div className="grid grid-rows-[auto_auto_1fr] gap-5 p-5 overflow-auto">
        <div className="text-xl font-bold space-x-2">
          <span
            onClick={() => handleBack()}
            onMouseEnter={() =>
              setHoverState((prev) => ({ ...prev, title: true }))
            }
            onMouseLeave={() =>
              setHoverState((prev) => ({ ...prev, title: false }))
            }
            style={
              breadcrumbState.folder
                ? { color: getDirectoryPrimaryColor(hoverState.title) }
                : {}
            }
            className={breadcrumbState.folder ? 'cursor-pointer' : ''}
          >
            {t('building.titleFileList')}
          </span>
          {folderType && (
            <>
              <RightArrowIcon color="black" size={20} />
              <span
                onClick={() => handleBack(true)}
                onMouseEnter={() =>
                  setHoverState((prev) => ({ ...prev, folder: true }))
                }
                onMouseLeave={() =>
                  setHoverState((prev) => ({ ...prev, folder: false }))
                }
                style={
                  breadcrumbState.children ||
                  breadcrumbState.children_folder ||
                  breadcrumbState.keyword
                    ? { color: getDirectoryPrimaryColor(hoverState.folder) }
                    : {}
                }
                className={breadcrumbState.children ? 'cursor-pointer' : ''}
              >
                {/* for default */}
                {annotationTypeValues.includes(Number(folderType)) &&
                  CommonFunction.getBreadcrumbTextByFolderType(folderType, t)}

                {/* for custom file */}
                {!annotationTypeValues.includes(Number(folderType)) &&
                  bcpCustomFolders?.find(
                    (item) => item.id === Number(folderType)
                  )?.name}
              </span>
              {childrenType && (
                <>
                  <RightArrowIcon color="#000000" size={20} />
                  <span>
                    {/* for default */}
                    {annotationTypeValues.includes(Number(folderType)) &&
                      CommonFunction.getBreadcrumbTextByChidType(
                        childrenType,
                        t,
                        getBuiildingType()
                      )}

                    {/* for custom file */}
                    {!annotationTypeValues.includes(Number(folderType)) &&
                      bcpCustomFolders
                        ?.find((item) => item.id === Number(folderType))
                        ?.childFolders?.find(
                          (item) => item.categoryId === Number(childrenType)
                        )?.name}
                  </span>
                </>
              )}

              {childrenFolderType && (
                <>
                  <RightArrowIcon color="#000000" size={20} />
                  <span>{childrenFolder}</span>
                </>
              )}
            </>
          )}
        </div>

        <ListFolder listFolder={convertedListFolder} />

        <ListChildrenFolder listFolder={convertedListFolder} />

        <ListFile
          listFolder={listFolder}
          setChildrenFolder={setChildrenFolder}
        />
      </div>
    </div>
  )
}
